/**
 * ToggleCard.jsx
 *
 * Generic on/off toggle card used by every DSC toggle:
 *   - TH `CouplingToggle` (Coupling required)
 *   - TH `BitAdapterToggle` (Bit adapters required — NEW)
 *   - DTH `UnitToggle` (Metric / Imperial)
 *   - Any future DSC toggles per brainstorm DTH #4 (shock absorbers,
 *     stabilisers, sampler tubes).
 *
 * Sandvik vibrant-orange (#F54B00) ON-state is a Marcom-approved exception
 * for the MRT app — preserves electric-blue exclusively for explicit CTA
 * buttons. Captured in MRT-PWA-Analysis/design-rules-from-walkthrough.md.
 *
 * Conditional availability (tracker #20): when `disabled=true`, the toggle
 * is visually dimmed and non-interactive. This prevents users from toggling
 * options that have no matching data in the current cascade state.
 *
 * @module components/dsc/ToggleCard
 * @since 2026-05-20 (Group E SP2 ��� shared DSC primitives)
 */

/**
 * @param {Object} props
 * @param {string} props.label
 * @param {string} [props.helperText]
 * @param {boolean} props.value
 * @param {(b: boolean) => void} props.onChange
 * @param {boolean} [props.disabled]
 * @param {string} [props.disabledReason]
 * @param {string} [props.dataTestid]
 * @param {string} [props.id]
 */
export default function ToggleCard({
  label,
  helperText,
  value,
  onChange,
  disabled = false,
  disabledReason,
  dataTestid = 'toggle-card',
  id,
}) {
  const inputId = id || `toggle-${(label || 'x').toLowerCase().replace(/\s+/g, '-')}`;

  return (
    <div
      data-testid={dataTestid}
      data-state={value ? 'on' : 'off'}
      data-disabled={disabled || undefined}
      className={`inline-flex items-center gap-3 px-4 py-2 border border-n3 bg-bg-paper rounded-none ${
        disabled ? 'opacity-50 cursor-not-allowed' : ''
      }`}
      title={disabled ? disabledReason : undefined}
    >
      <div className="flex flex-col">
        <label
          htmlFor={inputId}
          className={`text-sm font-sandvik-text font-normal text-carbon-black ${
            disabled ? 'cursor-not-allowed' : 'cursor-pointer'
          }`}
        >
          {label}
        </label>
        {helperText && (
          <span className="text-xs font-sandvik-text text-n5">{helperText}</span>
        )}
      </div>

      <button
        type="button"
        id={inputId}
        role="switch"
        aria-checked={!!value}
        aria-disabled={disabled || undefined}
        disabled={disabled}
        onClick={() => !disabled && onChange(!value)}
        data-testid={`${dataTestid}-switch`}
        className={`relative inline-flex h-6 w-11 items-center rounded-none border transition-colors focus:outline-none focus:ring-2 focus:ring-vibrant-orange ${
          disabled
            ? 'bg-n2 border-n3 cursor-not-allowed'
            : value
              ? 'bg-vibrant-orange border-vibrant-orange'
              : 'bg-n3 border-n5'
        }`}
      >
        <span
          aria-hidden="true"
          className={`inline-block h-4 w-4 transform bg-white transition-transform ${
            value ? 'translate-x-6' : 'translate-x-1'
          }`}
        />
      </button>
    </div>
  );
}
